'use strict';
import {getLink} from '../../core-utilities/domain/ReduxUtils';
import {ASSET_TASK_CLASS_NAME, ASSET_TASK_COLLECTION} from './config';


export const CLASS_NAME = ASSET_TASK_CLASS_NAME;
export const COLLECTION = ASSET_TASK_COLLECTION;

export function AssetTaskDomainProvider() {
  return {
    $get: [
      '$injector',
      'AuthService',
      'AbstractDomainClass',
      'restUtils',
      'FirebaseConnectionService',
      'FirebaseQueueService',
      '$$AbstractTask',
      'ROOT_API_COLLECTION',
      'ROOT_API_SPEC',
      function (
        $injector,
        AuthService,
        AbstractDomainClass,
        restUtils,
        FirebaseConnectionService,
        FirebaseQueueService,
        $$AbstractTask,
        ROOT_API_COLLECTION,
        ROOT_API_SPEC,
      ) {

        /**
         * @param {Object} data Serialized data
         * @class
         * @implements {HasMembersDomainClass}
         */
        function AssetTaskDomain(data) {
          AbstractDomainClass.call(this, AssetTaskDomain, data);
        }

        $$AbstractTask.__inheritFrom(AssetTaskDomain, CLASS_NAME, COLLECTION);

        AssetTaskDomain.$constraints.addConstraints({
          assetProject: {type: 'Domain', domainClass: 'AssetProjectDomain'},
          task: {type: 'Domain', domainClass: 'TaskDomain', nullable: true},
          ast: {type: 'string'}, // Asset ID

          members: {type: 'DomainCollection', domainClass: 'UserDomain', links: 'private-asset-tasks'},
          membersPersonnel: {type: 'DomainCollection', domainClass: 'PersonDomain', links: 'private-asset-tasks'},
          membersTeams: {type: 'DomainCollection', domainClass: 'TeamDomain', links: 'private-asset-tasks'},

          assetFormRequirements: {
            type: 'DomainCollection',
            domainClass: 'AssetFormRequirementDomain',
            link: 'assetTask',
            belongsTo: true
          },
          assetFileRequirements: {
            type: 'DomainCollection',
            domainClass: 'AssetFileRequirementDomain',
            link: 'assetTask',
            belongsTo: true
          }
        });

        // for fallback API methods
        AssetTaskDomain.fallbackIdProperty = 'taskId';
        AssetTaskDomain.createFallbackMethod = 'asset-tasks/create';
        AssetTaskDomain.updateFallbackMethod = 'asset-tasks/update';
        AssetTaskDomain.updateStatusFallbackMethod = 'asset-tasks/status/update';

        //TODO: add scheduled date change log

        // AssetProjectTask > AssetReqs

        // more methods go here;

        // used for create controllers that need to 'upgrade' a asset task in the view to a normal task
        AssetTaskDomain.createTaskFromAssetTask = function createTaskFromAssetTask(assetTask) {
          var TaskDomain = $injector.get('TaskDomain');
          var task = TaskDomain.$factory.create();
          task.assetSpecific = true;
          task.name = assetTask.name;
          task.description = assetTask.description;
          task.milestone = assetTask.milestone;
          task.project = assetTask.assetProject && assetTask.assetProject.project || null;

          return task;
        };

        AssetTaskDomain.prototype.create = restUtils.createProto(AssetTaskDomain, {
          getBody: (instance, options) => ({
            name: instance.name,
            description: instance.description,
            isPrivate: instance.isPrivate,
            scheduled: instance.scheduled ? instance.scheduled.getTime() : null,
            useTime: instance.useTime,
            duration: instance.duration,
            assignedTo: instance.assignedTo ? getLink(instance, 'assignedTo') : null,
            milestoneId: instance.milestone ? instance.milestone.$id : null,
            assetProjectId: instance.assetProject && instance.assetProject.$id,
            projectId: instance.project && instance.project.$id,
            orgId: instance.project && getLink(instance.project, 'organization').id,
            templateOptions: options || null,
          }),
        });

        AssetTaskDomain.prototype.update = restUtils.updateProto(AssetTaskDomain);

        AssetTaskDomain.update = restUtils.updateStatic(ASSET_TASK_COLLECTION/*, {
         afterSuccess,
         }*/);

        AssetTaskDomain.changePersonRole = changePersonRoleStatic;
        AssetTaskDomain.prototype.changePersonRole = changePersonRoleProto;
        AssetTaskDomain.changeTeamRole = changeTeamRoleStatic;
        AssetTaskDomain.prototype.changeTeamRole = changeTeamRoleProto;
        AssetTaskDomain.removePersonRole = removePersonRoleStatic;
        AssetTaskDomain.prototype.removePersonRole = removePersonRoleProto;
        AssetTaskDomain.removeTeamRole = removeTeamRoleStatic;
        AssetTaskDomain.prototype.removeTeamRole = removeTeamRoleProto;
        AssetTaskDomain.prototype.toString = toStringProto;
        AssetTaskDomain.prototype.getDescription = getDescriptionProto;
        AssetTaskDomain.prototype.toDescriptiveString = toDescriptiveStringProto;

        function changePersonRoleProto(personId, role) {
          return changePersonRoleStatic(this.$id, personId, role);
        }

        function changePersonRoleStatic(id, personId, role) {
          const request = {
            method: 'asset-tasks/members/change-person',
            task: id,
            targetProfile: personId,
            role: role
          };

          return FirebaseQueueService.addTask(request, AuthService.getUserId(), ROOT_API_COLLECTION, ROOT_API_SPEC);
        }

        function changeTeamRoleProto(teamId, role) {
          return changeTeamRoleStatic(this.$id, teamId, role);
        }

        function changeTeamRoleStatic(id, teamId, role) {
          const request = {
            method: 'asset-tasks/members/change-team',
            task: id,
            targetTeam: teamId,
            role: role
          };

          return FirebaseQueueService.addTask(request, AuthService.getUserId(), ROOT_API_COLLECTION, ROOT_API_SPEC);
        }

        function removePersonRoleProto(personId) {
          return removePersonRoleStatic(this.$id, personId);
        }

        function removePersonRoleStatic(id, personId) {
          const request = {
            method: 'asset-tasks/members/remove-person',
            task: id,
            targetProfile: personId
          };

          return FirebaseQueueService.addTask(request, AuthService.getUserId(), ROOT_API_COLLECTION, ROOT_API_SPEC);
        }

        function removeTeamRoleProto(teamId) {
          return removeTeamRoleStatic(this.$id, teamId);
        }

        function removeTeamRoleStatic(id, teamId) {
          const request = {
            method: 'asset-tasks/members/remove-team',
            task: id,
            targetTeam: teamId
          };

          return FirebaseQueueService.addTask(request, AuthService.getUserId(), ROOT_API_COLLECTION, ROOT_API_SPEC);
        }

        function toStringProto() {
          return this.task ? this.task.name : this.name;
        }

        function getDescriptionProto() {
          if (this.task) {
            return this.description ? this.description : this.task.description;
          } else {
            return this.description;
          }
        }

        function toDescriptiveStringProto() {
          return `${this} - ${this.assetProject ? this.assetProject.getFullNameSync() : this.assetProject}`
        }

        // TODO: create convenience methods to easily make asset specific clones of the parent task's requirements.

        function afterSuccess(instance) {
          // TODO: trigger update of appropriate epic if parentId matches after conversation to redux.
        }

        return AssetTaskDomain;
      }]
  };
}



// WEBPACK FOOTER //
// ./src/js/tasks/domain/AssetTaskDomain.js